<template>
  <div class="iv-placed-check el-input-group el-input-group--prepend">
    <div class="el-input-group__prepend">Is IV {{ isPlaced ? 'placed' : 'removed' }} with</div>
    <div class="options-group">
      <el-select
        v-close-on-zoom
        :value="state.check.category"
        placeholder="any type"
        class="type-select"
        :disabled="readonly"
        @input="category => updateCategory(category)"
      >
        <el-option
          v-for="atype in types"
          :key="atype.key"
          :label="atype.label.en"
          :value="atype.key"
        />
      </el-select>
      <help-tooltip v-if="typeWarning" type="warning">
        {{ typeWarning.message }}
      </help-tooltip>
      <el-select
        v-close-on-zoom
        :value="state.check.sites"
        placeholder="any site"
        multiple
        class="sites-select"
        :disabled="readonly"
        @input="sites => updateSites(sites)"
      >
        <el-option
          v-for="site in sites"
          :key="site.key"
          :label="site.label.en"
          :value="site.key"
        />
      </el-select>
      <help-tooltip v-if="siteWarning" type="warning">
        {{ siteWarning.message }}
      </help-tooltip>
      <el-select
        v-close-on-zoom
        :value="state.check.sides"
        placeholder="any side"
        multiple
        class="sides-select"
        :disabled="sidesDisabled || readonly"
        @input="sides => updateSides(sides)"
      >
        <el-option
          v-for="side in sides"
          :key="side.key"
          :label="side.label.en"
          :value="side.key"
        />
      </el-select>
      <help-tooltip v-if="sideWarning" type="warning">
        {{ sideWarning.message }}
      </help-tooltip>
      <br>
      <div v-if="isPlaced">
        <el-select
          v-close-on-zoom
          :value="state.check.needleGauges"
          placeholder="any needle gauge"
          multiple
          class="needle-gauges-select"
          :disabled="isIOOnly || readonly"
          @input="needleGauges => updateNeedleGauges(needleGauges)"
        >
          <el-option
            v-for="needleGauge in needleGauges"
            :key="needleGauge.key"
            :label="needleGauge.label.en"
            :value="needleGauge.key"
          />
        </el-select>
        <help-tooltip v-if="gaugeWarning" type="warning">
          {{ gaugeWarning.message }}
        </help-tooltip>
        <el-select
          v-close-on-zoom
          :value="state.check.needleSets"
          placeholder="any needle set"
          multiple
          class="needle-sets-select"
          :disabled="!hasIO || state.check.isSalineLock || readonly"
          @input="needleSets => updateNeedleSets(needleSets)"
        >
          <el-option
            v-for="needleSet in needleSets"
            :key="needleSet.key"
            :label="needleSet.label.en"
            :value="needleSet.key"
          />
        </el-select>
        <help-tooltip v-if="setWarning" type="warning">
          {{ setWarning.message }}
        </help-tooltip>
        <el-select
          v-close-on-zoom
          :value="state.check.ports"
          placeholder="any port"
          multiple
          class="ports-select"
          :disabled="isIOOnly || readonly"
          @input="ports => updatePorts(ports)"
        >
          <el-option
            v-for="port in ports"
            :key="port.key"
            :label="port.label.en"
            :value="port.key"
          />
        </el-select>
        <help-tooltip
          v-if="portWarning"
          class="help-tooltip"
          type="warning"
        >
          {{ portWarning.message }}
        </help-tooltip>
        <br>
        <el-checkbox
          label="Saline Lock ?"
          :value="state.check.isSalineLock"
          :disabled="readonly"
          class="saline-lock-checkbox"
          @input="isSalineLock => updateIsSalineLock(isSalineLock)"
        />
        <help-tooltip
          v-if="salineLockWarning"
          class="help-tooltip"
          type="warning"
        >
          {{ salineLockWarning.message }}
        </help-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';

import HelpTooltip from '~lib/common/components/HelpTooltip';
import iv from '~lib/common/config/iv';

export default {
  name: 'iv-placed-check',
  components: {
    HelpTooltip,
  },
  props: [
    'readonly',
    'emitter',
    'ikey',
    'getData',
    'putData',
    'control',
    'checkType',
  ],
  data() {
    return {
      state: {
        check: {
          category: null,
          sides: [],
          sites: [],
          needleGauges: [],
          needleSets: [],
          ports: [],
          isSalineLock: false,
          isCurrent: false,
        },
      },
    };
  },
  computed: {
    caseIvTypes() {
      const iv = this.emitter.caseConf.iv;
      return iv.types || [];
    },
    types() {
      const { isGlobal } = this.emitter;
      if (isGlobal) {
        return iv.categories;
      }
      return iv.categories.filter(
        category => this.caseIvTypes.find(caseType => caseType.type === category.key)
      ).concat({
        key: 'ANY',
        label: {
          en: 'Any type',
          es: 'Any type'
        }
      });
    },
    sides() {
      const { isGlobal } = this.emitter;
      if (isGlobal) {
        return iv.sides;
      }
      let availableSides = [];
      for (const type of this.caseIvTypes) {
        for (const site of type.sites) {
          availableSides = availableSides.concat(site.sides);
        }
      }
      return iv.sides.filter(s => uniq(availableSides).includes(s.key));
    },
    sites() {
      const { isGlobal } = this.emitter;
      if (isGlobal) {
        return iv.sites;
      }
      let availableSites = [];
      for (const ivType of this.caseIvTypes) {
        for (const site of ivType.sites) {
          availableSites = availableSites.concat(site.site);
        }
      }
      availableSites = uniq(availableSites);
      const category = this.state.check.category;
      return iv.sites.filter(s => {
        return (!category || category === s.category) && availableSites.includes(s.key);
      }
      );
    },
    needleGauges() {
      let availableGauges = [];
      for (const type of this.caseIvTypes) {
        availableGauges = availableGauges.concat(type.gauges);
      }
      return iv.needleGauges.filter(g => uniq(availableGauges).includes(g.key));
    },
    needleSets() {
      let availableSets = [];
      for (const type of this.caseIvTypes) {
        availableSets = availableSets.concat(type.gauges);
      }
      return iv.needleSets.filter(s => availableSets.includes(s.key));
    },
    ports() {
      const { isGlobal } = this.emitter;
      if (isGlobal) {
        return iv.portAmounts;
      }
      return iv.portAmounts.filter(
        port => this.caseIvTypes.find(caseType => caseType.ports.includes(port.key))
      );
    },
    ioSites() {
      return iv.sites
        .filter(site => site.category === 'INTRAOSSEOUS')
        .map(site => site.key);
    },
    hasIO() {
      const sites = this.state.check.sites || [];
      return sites.length === 0 || !!sites.find(site => this.ioSites.includes(site));
    },
    isIOOnly() {
      const sites = this.state.check.sites || [];
      return (
        sites.length > 0 &&
        !sites.find(site => !this.ioSites.includes(site))
      );
    },
    sidesDisabled() {
      const sites = this.state.check.sites;
      return sites.includes('IV_SCALP') || sites.includes('IO_STERNUM');
    },
    isPlaced() {
      return this.checkType === 'IV_PLACED';
    },
    warnings() {
      if (!this.emitter.$store) {
        return [];
      }
      return uniqBy(
        get(
          this.emitter.$store.getters['validation/visibleWarningsByFieldAndId'],
          `caseLogicDescription.${this.control.parent.id}`,
        ),
        'message',
      ).filter(
        warning =>
          warning.metadata &&
          warning.metadata.scope === 'control' &&
          warning.metadata.checkId === this.ikey,
      );
    },
    typeWarning() {
      return this.warnings.find(
        warning => warning.metadata.componentId === 'type',
      );
    },
    salineLockWarning() {
      return this.warnings.find(
        warning => warning.metadata.componentId === 'salineLock',
      );
    },
    sideWarning() {
      return this.warnings.find(
        warning => warning.metadata.componentId === 'side',
      );
    },
    siteWarning() {
      return this.warnings.find(
        warning => warning.metadata.componentId === 'site',
      );
    },
    gaugeWarning() {
      return this.warnings.find(
        warning => warning.metadata.componentId === 'gauge',
      );
    },
    setWarning() {
      return this.warnings.find(
        warning => warning.metadata.componentId === 'set',
      );
    },
    portWarning() {
      return this.warnings.find(
        warning => warning.metadata.componentId === 'port',
      );
    },
  },
  beforeMount() {
    this.state.check = { ...this.state.check, ...this.getData(this.ikey) };
    this.update();
  },
  methods: {
    updateCategory(category) {
      this.state.check.category = category === 'ANY' ? undefined : category;
      this.state.check.sites = [];
      this.state.check.ports = [];
      this.update();
    },
    updateSides(sides) {
      this.state.check.sides = sides;
      this.update();
    },
    updateSites(sites) {
      this.state.check.sites = sites;
      if (this.sidesDisabled) {
        this.state.check.sides = [];
      }
      if (!this.hasIO) {
        this.state.check.needleSets = [];
      }
      if (this.isIOOnly) {
        this.state.check.needleGauges = [];
      }
      this.update();
    },
    updateNeedleGauges(needleGauges) {
      this.state.check.needleGauges = needleGauges;
      this.update();
    },
    updateNeedleSets(needleSets) {
      this.state.check.needleSets = needleSets;
      this.update();
    },
    updatePorts(ports) {
      this.state.check.ports = ports;
      this.update();
    },
    updateIsSalineLock(isSalineLock) {
      this.state.check.isSalineLock = isSalineLock;
      if (isSalineLock) {
        this.updateNeedleSets([]);
      }
      this.update();
    },
    update: function update() {
      this.putData(this.ikey, {
        type: this.checkType,
        id: 'IV',
        operator: 'ADMINISTERED',
        value: true,
        ...this.state.check,
      });
      this.emitter.trigger('process', this.control);
    },
  },
};
</script>

<style scoped>
.options-group {
  /* display: flex; */
  background-color: white;
}

.el-input-group {
  width: auto;
}

.help-tooltip {
  margin: 0 4px;
}

.saline-lock-checkbox {
  margin: 4px 0 4px 0;
}

</style>
